import { Link } from "react-router";
import { FiArrowLeft, FiClock } from "react-icons/fi";
import { FaShopify } from "react-icons/fa";

// Image paths - these will be served from public folder
const IMAGES = {
  appAndSalesChannel: "/docs/shopify-setup/app-and-sales-channel-setting.png",
  developAppButton: "/docs/shopify-setup/develop-app-button.png",
  createAppButton: "/docs/shopify-setup/create-app-button.png",
  appDetails: "/docs/shopify-setup/app-details.png",
  adminAccessButton: "/docs/shopify-setup/admin-access-button.png",
  adminApiAccess: "/docs/shopify-setup/admin-api-access.png",
  installApp: "/docs/shopify-setup/install-app.png",
  apiAccessToken: "/docs/shopify-setup/api-access-token.png",
};

const API_SCOPES = [
  {
    scope: "read_orders",
    uiName: "Orders",
    description: "Analyze order trends, COD vs Prepaid, AOV",
  },
  {
    scope: "read_products",
    uiName: "Products",
    description: "Product performance analytics",
  },
  {
    scope: "read_inventory",
    uiName: "Inventory",
    description: "Stock level insights",
  },
  {
    scope: "read_locations",
    uiName: "Locations",
    description: "Multi-location inventory tracking",
  },
  {
    scope: "read_fulfillments",
    uiName: "Fulfillments",
    description: "Shipping and delivery analytics",
  },
  {
    scope: "read_checkouts",
    uiName: "Checkouts",
    description: "Abandoned checkout analysis",
  },
  {
    scope: "read_discounts",
    uiName: "Discounts",
    description: "Discount code performance",
  },
  {
    scope: "read_price_rules",
    uiName: "Price rules",
    description: "Pricing strategy analysis",
  },
  {
    scope: "read_draft_orders",
    uiName: "Draft orders",
    description: "B2B and manual order insights",
  },
  {
    scope: "read_returns",
    uiName: "Returns",
    description: "Return rate and RTO analysis",
  },
];

function Screenshot({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="my-6">
      <img
        src={src}
        alt={alt}
        className="w-full border border-base-300 rounded-lg shadow-md"
      />
    </div>
  );
}

export default function ShopifySetupGuide() {
  return (
    <div className="h-screen overflow-y-auto bg-base-100">
      {/* Header */}
      <header className="py-6 px-8 lg:px-16 border-b border-base-200 sticky top-0 bg-base-100/95 backdrop-blur-sm z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-primary">
            Gamgee Analytics
          </Link>
          <Link to="/" className="btn btn-ghost btn-sm gap-2">
            <FiArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="py-12 px-8 lg:px-16">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <FaShopify className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-3xl lg:text-4xl font-bold">
                How to Create a Shopify Custom App and Connect to Gamgee
              </h1>
            </div>

            <div className="alert bg-info/10 border-l-6 border-info">
              <div>
                <p className="font-semibold">What you'll accomplish:</p>
                <p className="text-sm text-base-content/70">
                  Create a custom app in your Shopify store to generate an API
                  Access Token. This token allows Gamgee to securely read your
                  store data (orders, products, inventory) for analytics —
                  without any write access to your store.
                </p>
              </div>
            </div>
          </header>

          {/* Prerequisites */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold mb-4">Prerequisites</h2>
            <p className="text-base-content/70 mb-2">
              Before you begin, make sure you have:
            </p>
            <ul className="list-disc list-inside text-base-content/70">
              <li>
                Owner or staff access to your Shopify store with{" "}
                <strong>"Develop apps"</strong> permission
              </li>
            </ul>
          </section>

          <div className="divider"></div>

          {/* Step 1: Navigate to App Development */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold mb-6">
              Step 1: Navigate to App Development
            </h2>

            <p className="text-base-content/70 mb-4">
              Log in to your <strong>Shopify Admin</strong> and use the search
              bar to find <strong>"Apps and sales channels"</strong> settings.
            </p>

            <Screenshot
              src={IMAGES.appAndSalesChannel}
              alt="Search for Apps and sales channels settings"
            />

            <p className="text-base-content/70 mb-4">
              Click the <strong>"Develop apps"</strong> button in the top right
              corner.
            </p>

            <Screenshot
              src={IMAGES.developAppButton}
              alt="Develop apps button"
            />

            <div className="alert alert-warning">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="stroke-current shrink-0 h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span>
                <strong>Note:</strong> If you see "Allow custom app development"
                instead of "Develop apps", click it first to enable custom app
                development for your store.
              </span>
            </div>
          </section>

          <div className="divider"></div>

          {/* Step 2: Create a Custom App */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold mb-6">
              Step 2: Create a Custom App
            </h2>

            <p className="text-base-content/70 mb-4">
              Click the <strong>"Create an app"</strong> button in the top right
              corner.
            </p>

            <Screenshot
              src={IMAGES.createAppButton}
              alt="Create an app button"
            />

            <p className="text-base-content/70 mb-4">
              Enter the app name:{" "}
              <code className="bg-base-200 px-2 py-0.5 rounded">
                Gamgee Analytics
              </code>{" "}
              (or any name you prefer) and click <strong>"Create app"</strong>.
            </p>

            <Screenshot
              src={IMAGES.appDetails}
              alt="Create app modal with app name field"
            />
          </section>

          <div className="divider"></div>

          {/* Step 3: Configure API Scopes */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold mb-6">
              Step 3: Configure API Scopes (Permissions)
            </h2>

            <p className="text-base-content/70 mb-4">
              Now we'll set up which data Gamgee can access. We only need{" "}
              <strong>read permissions</strong> — no write access.
            </p>

            <p className="text-base-content/70 mb-4">
              In the app Overview tab, click{" "}
              <strong>"Configure Admin API scopes"</strong>.
            </p>

            <Screenshot
              src={IMAGES.adminAccessButton}
              alt="Configure Admin API scopes link in Overview tab"
            />

            <p className="text-base-content/70 mb-4">
              Search for and select the following <strong>read-only</strong>{" "}
              scopes:
            </p>

            <Screenshot
              src={IMAGES.adminApiAccess}
              alt="Admin API access scopes selection page"
            />

            <div className="overflow-x-auto mb-6">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Scope</th>
                    <th>Permission Name in UI</th>
                    <th>What it's used for</th>
                  </tr>
                </thead>
                <tbody>
                  {API_SCOPES.map((item) => (
                    <tr key={item.scope}>
                      <td>
                        <code className="bg-base-200 px-2 py-0.5 rounded text-sm">
                          {item.scope}
                        </code>
                      </td>
                      <td>{item.uiName}</td>
                      <td className="text-base-content/70">
                        {item.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="alert alert-warning mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="stroke-current shrink-0 h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span>
                <strong>Important:</strong> Only select <strong>read_</strong>{" "}
                permissions. Do NOT select any <strong>write_</strong>{" "}
                permissions. Gamgee will reject tokens with write access for
                your security.
              </span>
            </div>

            <p className="text-base-content/70">
              After selecting all scopes, click <strong>"Save"</strong> at the
              top of the page.
            </p>
          </section>

          <div className="divider"></div>

          {/* Step 4: Install the App and Get Your Access Token */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold mb-6">
              Step 4: Install the App and Get Your Access Token
            </h2>

            <p className="text-base-content/70 mb-4">
              Go to the <strong>"API credentials"</strong> tab and click{" "}
              <strong>"Install app"</strong> in the Access tokens section.
            </p>

            <Screenshot
              src={IMAGES.installApp}
              alt="API credentials tab with Install app button"
            />

            <p className="text-base-content/70 mb-4">
              Click <strong>"Install"</strong> in the confirmation modal.
            </p>

            <p className="text-base-content/70 mb-4">
              Click <strong>"Reveal token once"</strong> to see your Admin API
              access token, then click the <strong>copy icon</strong> to copy it
              to your clipboard.
            </p>

            <Screenshot
              src={IMAGES.apiAccessToken}
              alt="Revealed Admin API access token"
            />

            <div className="alert alert-error">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="stroke-current shrink-0 h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span>
                <strong>⚠️ Important:</strong> The token is only shown{" "}
                <strong>once</strong>. Copy it immediately and store it safely.
                If you lose it, you'll need to uninstall and reinstall the app
                to generate a new token.
              </span>
            </div>
          </section>

          <div className="divider"></div>

          {/* Step 5: Enter Your Shopify Details in Gamgee */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold mb-6">
              Step 5: Enter Your Shopify Details in Gamgee
            </h2>

            <p className="text-base-content/70 mb-4">
              Enter the following details in the Shopify connection form:
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>What to enter</th>
                    <th>Example</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="font-semibold">Shop Name</td>
                    <td>Your myshopify.com domain (without https://)</td>
                    <td>
                      <code className="bg-base-200 px-2 py-0.5 rounded text-sm">
                        your-store.myshopify.com
                      </code>
                    </td>
                  </tr>
                  <tr>
                    <td className="font-semibold">API Version</td>
                    <td>Use the latest stable version</td>
                    <td>
                      <code className="bg-base-200 px-2 py-0.5 rounded text-sm">
                        2025-01
                      </code>
                    </td>
                  </tr>
                  <tr>
                    <td className="font-semibold">Access Token</td>
                    <td>Paste the token you copied from Shopify</td>
                    <td>
                      <code className="bg-base-200 px-2 py-0.5 rounded text-sm">
                        shpat_xxxxxxxxxxxxxxxx
                      </code>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <div className="divider"></div>

          {/* Troubleshooting */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold mb-6">Troubleshooting</h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">
                  ❌ "Connection validation failed" error
                </h3>
                <p className="text-base-content/70">
                  This usually means your access token has write permissions. Go
                  back to your Shopify app, check the API scopes, and ensure
                  you've only selected <strong>read_</strong> permissions (not
                  write_).
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2">
                  ❌ "Invalid shop domain" error
                </h3>
                <p className="text-base-content/70">
                  Make sure you're entering just{" "}
                  <code className="bg-base-200 px-2 py-0.5 rounded">
                    your-store.myshopify.com
                  </code>{" "}
                  without{" "}
                  <code className="bg-base-200 px-2 py-0.5 rounded">
                    https://
                  </code>{" "}
                  or trailing slashes.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2">
                  ❌ Lost your access token
                </h3>
                <p className="text-base-content/70 mb-2">
                  If you didn't copy the token before closing the page:
                </p>
                <ol className="list-decimal list-inside text-base-content/70 space-y-1">
                  <li>
                    Go to Settings → Apps and sales channels → Develop apps
                  </li>
                  <li>Click on your "Gamgee Analytics" app</li>
                  <li>Go to "API credentials" tab</li>
                  <li>Click "Uninstall app"</li>
                  <li>Click "Install app" again to generate a new token</li>
                </ol>
              </div>
            </div>
          </section>

          <div className="divider"></div>

          {/* FAQ */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold mb-6">FAQ</h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">
                  Is my data secure?
                </h3>
                <p className="text-base-content/70">
                  Yes. Your access token is encrypted using industry-standard
                  encryption before being stored. Gamgee only requests read-only
                  access and cannot modify your store data.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2">
                  Can I revoke access later?
                </h3>
                <p className="text-base-content/70">
                  Yes. Simply go to your Shopify Admin → Settings → Apps and
                  sales channels → Develop apps → Your app → Uninstall app. This
                  immediately revokes Gamgee's access.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2">
                  How often is data synced?
                </h3>
                <p className="text-base-content/70">
                  Data is synced automatically once every day.
                </p>
              </div>
            </div>
          </section>

          <div className="divider"></div>

          {/* Need Help */}
          <section className="text-center py-8">
            <p className="text-base-content/60">
              Need help? Contact us at{" "}
              <a href="mailto:support@gamgee.ai" className="link link-primary">
                support@gamgee.ai
              </a>
            </p>
          </section>

          {/* Back to Home */}
          <div className="pt-8 border-t border-base-200">
            <Link to="/" className="btn btn-outline gap-2">
              <FiArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-8 lg:px-16 bg-base-200 text-center">
        <p className="text-sm opacity-60">
          &copy; {new Date().getFullYear()} Gamgee Analytics. All rights
          reserved.
        </p>
      </footer>
    </div>
  );
}
